/** Where a visitor's own choice about animation is kept, in the browser's local storage. */
export const MOTION_STORAGE_KEY = "adkiro-motion";

export type MotionChoice = "play" | "still";

/**
 * Decides, before the first paint, whether this visit gets the animated site
 * or the still one, and marks <html> with .motion-ok or .motion-reduce.
 *
 * Animated, unless the visitor has turned the animation off on the site (the
 * switch in the footer). The device's own "reduce motion" setting is not
 * consulted. The first release did consult it, and gave such a device the
 * still site with a Play button. Kristian's Mac is set that way and, by what
 * he saw, so is his phone: the site opened still for him everywhere, and on
 * 3 Oct 2026 he objected that it opened empty and had to be clicked before
 * anything moved. What a visitor who wants less motion has instead is the
 * switch, on every page, and the answer is kept.
 *
 * It runs as a script in <head> rather than in React because the two layouts
 * differ in height: the homepage stage is one screen when still and several
 * when animated. Deciding after hydration would let the page be drawn one
 * way and then jump to the other.
 *
 * No class at all means scripts did not run, and the stylesheet treats that
 * as the still site, which is what a crawler reads.
 */
export const MOTION_BOOTSTRAP = `(function(){try{var c=null;try{c=window.localStorage.getItem("${MOTION_STORAGE_KEY}")}catch(e){}document.documentElement.classList.add(c==="still"?"motion-reduce":"motion-ok")}catch(e){}})();`;

/**
 * Whether this browser will keep a choice at all. One set to block site data
 * throws at the first touch of its storage, and a switch that cannot be kept
 * is better not offered.
 */
export function canKeepMotionChoice(): boolean {
  try {
    window.localStorage.getItem(MOTION_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * Keeps the visitor's choice and loads the page again from the top, so the
 * script above lays it out for that choice before anything is painted, as it
 * will on every later page and visit.
 *
 * False when the browser refused to keep it. Nothing has happened then, and
 * the caller can at least honour the choice for the page that is open.
 */
export function switchMotion(choice: MotionChoice): boolean {
  try {
    window.localStorage.setItem(MOTION_STORAGE_KEY, choice);
  } catch {
    return false;
  }
  window.scrollTo(0, 0);
  window.location.reload();
  return true;
}
